import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import {
    Modal,
    Box,
    Typography,
    IconButton,
    CircularProgress,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Alert
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import { viewFileContent, downloadFile } from 'store/slices/uploadedfile';

const style = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '90%',
    maxWidth: '1200px',
    maxHeight: '90vh',
    bgcolor: 'background.paper',
    boxShadow: 24,
    p: 4,
    overflow: 'auto',
    borderRadius: 2
};

const FileViewerModal = ({ open, onClose, fileName, filePath, fileType, folderName }) => {
    const dispatch = useDispatch();
    const [loading, setLoading] = useState(false);
    const [content, setContent] = useState(null);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (open && filePath) {
            fetchFileContent();
        }
    }, [open, filePath]);

    const fetchFileContent = async () => {
        setLoading(true);
        setError(null);

        try {
            // Use Redux thunk action
            const data = await dispatch(viewFileContent(folderName, filePath));
            setContent(data);
        } catch (err) {
            console.error('Error fetching file content:', err);
            setError(err.response?.data?.detail || err.message || 'Failed to fetch file content');
        } finally {
            setLoading(false);
        }
    };

    const handleDownload = async () => {
        try {
            // Use Redux thunk action
            await dispatch(downloadFile(folderName, filePath, fileName));
        } catch (error) {
            console.error('Error downloading file:', error);
            alert('Failed to download file. Please try again.');
        }
    };

    const renderContent = () => {
        if (loading) {
            return (
                <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
                    <CircularProgress />
                </Box>
            );
        }

        if (error) {
            return <Alert severity="error">{error}</Alert>;
        }

        if (!content) {
            return null;
        }

        // Render based on file type
        if ((fileType === 'csv' && content.type === 'csv') || (['xlsx', 'xls'].includes(fileType) && content.type === 'excel')) {
            if (content.error) {
                return <Alert severity="error">{content.error}</Alert>;
            }

            return (
                <Box>
                    {content.truncated && (
                        <Alert severity="info" sx={{ mb: 1 }}>
                            Showing first 100 rows. Total rows: {content.total_rows || '100+'}
                        </Alert>
                    )}
                    <TableContainer component={Paper} sx={{ maxHeight: '60vh' }}>
                        <Table stickyHeader>
                            <TableHead>
                                <TableRow>
                                    {content.headers.map((header, index) => (
                                        <TableCell key={index} sx={{ fontWeight: 'bold' }}>
                                            {header}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {content.rows.map((row, rowIndex) => (
                                    <TableRow key={rowIndex}>
                                        {row.map((cell, cellIndex) => (
                                            <TableCell key={cellIndex}>{cell}</TableCell>
                                        ))}
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Box>
            );
        }

        if (
            ['txt', 'log', 'json', 'xml', 'py', 'js', 'html', 'css'].includes(fileType) ||
            (['docx', 'doc'].includes(fileType) && content.type === 'docx')
        ) {
            if (content.error) {
                return <Alert severity="error">{content.error}</Alert>;
            }

            return (
                <Box>
                    {content.truncated && (
                        <Alert severity="info" sx={{ mb: 1 }}>
                            File content truncated. Showing first 50KB.
                        </Alert>
                    )}
                    <Paper sx={{ p: 2, maxHeight: '60vh', overflow: 'auto', backgroundColor: '#f5f5f5' }}>
                        <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{content.content}</pre>
                    </Paper>
                </Box>
            );
        }

        if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(fileType)) {
            return (
                <Box display="flex" justifyContent="center" alignItems="center">
                    <img src={content.content} alt={fileName} style={{ maxWidth: '100%', maxHeight: '60vh', objectFit: 'contain' }} />
                </Box>
            );
        }

        if (fileType === 'pdf') {
            return (
                <Box>
                    <Alert severity="info" sx={{ mb: 2 }}>
                        PDF preview is not available. Click download to view the file.
                    </Alert>
                    <Paper sx={{ p: 2, maxHeight: '60vh', overflow: 'auto' }}>
                        <Typography variant="body2">{content.preview || 'PDF content preview not available'}</Typography>
                    </Paper>
                </Box>
            );
        }

        return <Alert severity="warning">Preview not available for this file type ({fileType}). Click download to view the file.</Alert>;
    };

    return (
        <Modal open={open} onClose={onClose}>
            <Box sx={style}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <Typography variant="h5" component="h2">
                        {fileName}
                    </Typography>
                    <Box>
                        <IconButton onClick={handleDownload} color="primary" sx={{ mr: 1 }}>
                            <DownloadIcon />
                        </IconButton>
                        <IconButton onClick={onClose}>
                            <CloseIcon />
                        </IconButton>
                    </Box>
                </Box>

                {renderContent()}
            </Box>
        </Modal>
    );
};

export default FileViewerModal;
